import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { Link } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate } from 'utils/dateFormatter';
import { fmt } from 'utils/common';
import { SHOP_PATHS } from 'routes/routesConstants';

// ─── Type ─────────────────────────────────────────────────────────────────────

interface StockMovement {
  id: string;
  shopId: string;
  shopItemId: string;
  shopItemName?: string;
  quantity: number;
  movementType: string; // Sale | Purchase | Adjustment | Return | OpeningBalance
  referenceType: string; // SellInvoice | PurchaseInvoice | SellAdjustment | PurchaseAdjustment
  referenceId: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Plain-English "story" for what happened to stock.
 * Written so a common man immediately understands.
 */
function getStockStory(row: StockMovement): {
  icon: string;
  headline: string; // short bold label
  detail: string; // one-line plain explanation
  direction: 'in' | 'out' | 'neutral';
} {
  const { movementType, referenceType, quantity } = row;
  const isIn = quantity > 0;

  if (movementType === 'Sale')
    return {
      icon: '🛒',
      headline: 'Sold to customer',
      detail: 'Medicine went out — customer bought it',
      direction: 'out',
    };

  if (movementType === 'Purchase')
    return {
      icon: '📦',
      headline: 'Restocked from supplier',
      detail: 'New stock arrived from supplier — added to shelf',
      direction: 'in',
    };

  if (movementType === 'Return')
    return {
      icon: '↩',
      headline: 'Customer returned',
      detail: 'Customer brought medicine back — stock added again',
      direction: 'in',
    };

  if (movementType === 'Adjustment') {
    if (referenceType === 'SellAdjustment') {
      return isIn
        ? {
            icon: '↩',
            headline: 'Customer return (correction)',
            detail: 'Stock came back due to sale correction',
            direction: 'in',
          }
        : {
            icon: '📉',
            headline: 'Extra taken (correction)',
            detail: 'More stock went out due to sale correction',
            direction: 'out',
          };
    }
    if (referenceType === 'PurchaseAdjustment') {
      return isIn
        ? {
            icon: '📦',
            headline: 'Extra received (correction)',
            detail: 'More stock added due to purchase correction',
            direction: 'in',
          }
        : {
            icon: '🔁',
            headline: 'Returned to supplier',
            detail: 'Stock sent back to supplier',
            direction: 'out',
          };
    }
    return {
      icon: '✏️',
      headline: isIn ? 'Stock added (correction)' : 'Stock removed (correction)',
      detail: 'Manual correction applied to stock count',
      direction: isIn ? 'in' : 'out',
    };
  }

  if (movementType === 'OpeningBalance')
    return {
      icon: '🏁',
      headline: 'Opening balance',
      detail: 'Starting stock when the system went live',
      direction: 'in',
    };

  return {
    icon: '📋',
    headline: movementType,
    detail: row.notes || '—',
    direction: isIn ? 'in' : 'out',
  };
}

/**
 * Which screen to navigate to when user clicks the source reference.
 */
function getReferenceLink(row: StockMovement, shopId: string): string | null {
  switch (row.referenceType) {
    case 'SellInvoice':
    case 'SellAdjustment':
      return SHOP_PATHS.MEDICINE_SELL_INVOICE_DETAIL.replace(':shopId', shopId).replace(
        ':invoiceId',
        row.referenceId,
      );
    case 'PurchaseInvoice':
    case 'PurchaseAdjustment':
      return (
        SHOP_PATHS.PURCHASE_INVOICE_DETAIL?.replace(':shopId', shopId)?.replace(
          ':invoiceId',
          row.referenceId,
        ) ?? null
      );
    default:
      return null;
  }
}

const SOURCE_META: Record<string, { label: string; color: string; bg: string }> = {
  SellInvoice: { label: 'Sale invoice', color: '#059669', bg: '#ecfdf5' },
  PurchaseInvoice: { label: 'Purchase invoice', color: '#2563eb', bg: '#eff6ff' },
  SellAdjustment: { label: 'Sale correction', color: '#d97706', bg: '#fffbeb' },
  PurchaseAdjustment: { label: 'Purchase correction', color: '#7c3aed', bg: '#f5f3ff' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SourcePill: React.FC<{ type: string }> = ({ type }) => {
  const m = SOURCE_META[type] ?? { label: type, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: m.bg,
        color: m.color,
        whiteSpace: 'nowrap',
        lineHeight: 1.7,
      }}
    >
      {m.label}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const StockMovementList: React.FC = () => {
  const { shop } = useShop();
  const shopId = shop?.id ?? '';

  const columns = useMemo<DataTableColumn<StockMovement>[]>(
    () => [
      // ── What happened — icon + plain headline + detail ────────────────────────
      {
        accessorKey: 'movementType',
        id: 'whatHappened',
        header: 'What happened?',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<StockMovement, unknown>) => {
          const row = info.row.original;
          const { icon, headline, detail } = getStockStory(row);
          return (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 15,
                  flexShrink: 0,
                }}
              >
                {icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0d0f12' }}>{headline}</div>
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 1 }}>{detail}</div>
              </div>
            </div>
          );
        },
      },

      // ── Medicine name ─────────────────────────────────────────────────────────
      {
        accessorKey: 'shopItemName',
        id: 'shopItemName',
        header: 'Which medicine?',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<StockMovement, unknown>) => {
          const name = (info.getValue() as string) || '—';
          return <span style={{ fontSize: 13, fontWeight: 600, color: '#0d0f12' }}>{name}</span>;
        },
      },

      // ── Quantity — the most important number ──────────────────────────────────
      {
        accessorKey: 'quantity',
        id: 'quantity',
        header: 'How many?',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<StockMovement, unknown>) => {
          const row = info.row.original;
          const qty = info.getValue() as number;
          const { direction } = getStockStory(row);
          const isIn = direction === 'in';
          const isOut = direction === 'out';

          const color = isIn ? '#059669' : isOut ? '#dc2626' : '#374151';
          const bg = isIn ? '#ecfdf5' : isOut ? '#fef2f2' : '#f9fafb';
          const prefix = isIn ? '+' : '';
          const label = isIn ? 'Came in ↑' : isOut ? 'Went out ↓' : '';

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 10px',
                  borderRadius: 7,
                  background: bg,
                  width: 'fit-content',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    fontSize: 14,
                    fontWeight: 700,
                    color,
                  }}
                >
                  {prefix}
                  {Math.abs(qty)} pcs
                </span>
              </div>
              {label && (
                <span style={{ fontSize: 10.5, color, fontWeight: 500, paddingLeft: 2 }}>
                  {label}
                </span>
              )}
            </div>
          );
        },
      },

      // ── Source — what caused this movement ───────────────────────────────────
      {
        accessorKey: 'referenceType',
        id: 'referenceType',
        header: 'Reason / Source',
        enableSorting: true,
        enableFiltering: true,
        cell: (info: CellContext<StockMovement, unknown>) => {
          const row = info.row.original;
          const link = getReferenceLink(row, shopId);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <SourcePill type={info.getValue() as string} />
              {link ? (
                <Link
                  to={link}
                  style={{
                    fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    fontSize: 11,
                    color: '#2563eb',
                    textDecoration: 'none',
                  }}
                >
                  View details →
                </Link>
              ) : (
                <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>
                  {row.referenceId.slice(0, 8).toUpperCase()}…
                </span>
              )}
            </div>
          );
        },
      },

      // ── Notes ─────────────────────────────────────────────────────────────────
      {
        accessorKey: 'notes',
        id: 'notes',
        header: 'Extra notes',
        enableSorting: false,
        enableFiltering: true,
        cell: (info: CellContext<StockMovement, unknown>) => {
          const notes = info.getValue() as string | undefined;
          if (!notes) return <span style={{ color: '#d1d5db' }}>—</span>;
          return (
            <span
              title={notes}
              style={{
                fontSize: 12,
                color: '#6b7280',
                maxWidth: 200,
                display: 'inline-block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {notes}
            </span>
          );
        },
      },

      // ── Date ─────────────────────────────────────────────────────────────────
      {
        accessorKey: 'createdAt',
        id: 'createdAt',
        header: 'When?',
        enableSorting: true,
        enableFiltering: false,
        cell: (info: CellContext<StockMovement, unknown>) => {
          const { text, isStale } = formatRelativeDate(info.getValue() as string);
          return (
            <span style={{ fontSize: 12, color: isStale ? '#dc2626' : '#6b7280' }}>{text}</span>
          );
        },
      },
    ],
    [shopId],
  );

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<StockMovement>> => {
    return DataTableApiService.fetchPaginatedData<StockMovement>(
      API_ENDPOINTS.TENANT.STOCK_MOVEMENTS.PAGINATED.replace(':shopId', shopId),
      { ...params, filters: { ...params.filters, shopId } },
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            {/* Header */}
            <div style={{ marginBottom: 14 }}>
              <h5 style={{ margin: 0, fontWeight: 600 }}>Stock movements</h5>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                Every time medicine came in or went out — permanent record, never changed.
              </p>
            </div>

            {/* Quick legend — even a common man reads this */}
            <div
              style={{
                display: 'flex',
                gap: 14,
                flexWrap: 'wrap',
                alignItems: 'center',
                padding: '8px 12px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                marginBottom: 14,
                fontSize: 12,
              }}
            >
              <span style={{ color: '#6b7280', fontWeight: 500 }}>How to read:</span>
              <span>
                📦 <strong style={{ color: '#059669' }}>Green +</strong> = stock came in (aaya)
              </span>
              <span>
                🛒 <strong style={{ color: '#dc2626' }}>Red −</strong> = stock went out (gaya)
              </span>
              <span>
                ↩ <strong style={{ color: '#7c3aed' }}>Purple</strong> = correction or return
              </span>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.STOCK_MOVEMENTS.PAGINATED.replace(':shopId', shopId)}
              onFetchData={handleFetchData}
              enableSearch
              enableFiltering
              enablePagination
              enableSorting
              enableColumnVisibility
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={20}
              columnVisibilityStorageKey="stock_movements_visibility"
              columnOrderStorageKey="stock_movements_order"
              title=""
              emptyMessage="No stock movements found."
              striped
              hover
              bordered
              responsive
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default StockMovementList;
